/* eslint-disable no-underscore-dangle */
/**
 * `/portal/dispatcher/trip/:bookingId` — one trip on its own screen, in
 * English.
 *
 * Opened from a row's Details on the board, which hands the row over in
 * router state so the screen paints at once. Either way the trip is then
 * read ONCE from `/portal/dispatcher/board` — its own Budapest day when the
 * row is known, else a window around today (a deep link or a pasted URL) —
 * so a reload never shows a stale driver or step.
 *
 * Everything on it is the driver portal's own components with the operator
 * screens' English copy (the `copy` props from operatorFormat.js): the route
 * map with the programme's stops, the route rail, the customer (the phone
 * only inside the API's 24-hour window, else when it appears), the office's
 * note for that slot, the passenger manifest and the overtime. They are the
 * same components the driver and the operator see — not a third variant.
 *
 * IT OPENS ON WHAT IS NEEDED FIRST. Every long section folds
 * (CollapsibleSection, the board's own) and says what it holds while closed,
 * but they do not all start the same way: where the trip GOES — the route
 * map and the stops — is open on arrival, because that is what a dispatcher
 * opens the screen to see. The passenger manifest and the paid-waiting
 * entries start closed; they are reference, read when something is in
 * question. Each remembers the person's own answer either way, so anyone
 * who wants the opposite gets it from then on. The crew, the customer and
 * the office's note are never folded at all.
 *
 * IT WRITES TWO THINGS. WHO IS ON THE TRIP: the Crew card's More… opens the
 * Crew sheet, and from there Assign / Change opens the portal's shared
 * AssignSheet with the office's source — the same dialog and the same
 * endpoint as the board's rows — and what comes back replaces the trip on
 * screen. And A MESSAGE TO THE PASSENGER, sent as the company: the customer
 * card's own green button (PassengerMessage, in its `primary` slot), which
 * is emphatically not a `wa.me` deep link and says so.
 *
 * ITS ACTIONS ARE TIERED, because a phone is not a desk. BOTH cards now have
 * the same two tiers — the thing the office does, on the card and dominant;
 * everything else one tap away in a sheet:
 *
 *   Crew      Message the driver and Call on the card; editing the crew and
 *             acting as the driver behind More… (CrewSheet).
 *   Customer  Message as the office and Call on the card; the sender's own
 *             `wa.me` chat behind More options (PassengerMoreOptions).
 *
 * The passenger's two are NOT in the Crew sheet: that one is what you can do
 * to the crew, and the passenger is not crew. Both components hold the
 * reasoning. Only the dispatcher fills those slots — `POST /portal/message`
 * 403s a driver or an operator — which is why CustomerCard takes nodes
 * rather than a role to compare against.
 *
 * IT ALSO SHOWS THE TWO THINGS A BOARD ROW DOES NOT, from
 * `/portal/dispatcher/trip` — a second, small read alongside the board one:
 *
 * A TRIP THAT ENDED IN A NO-SHOW opens with why. The claim is against the
 * customer and the partner will contest it, so the grounds the driver filed
 * — which clock allowed it, the steps they confirmed, the call attempts the
 * app recorded, their note and where the phone was — sit at the top of the
 * screen rather than folded away. Not the photos: the dispatcher stack
 * deliberately has no access to the private bucket they live in, so it
 * shows how many there are and the dashboard's Driver events tab shows
 * them.
 *
 *   Sold by            the partner's name, and their number and address as a
 *                      tap-to-call and a tap-to-write, so the office can
 *                      chase Booking.com about THIS trip from a phone. Pickup
 *                      Hungary's own booking reads "direct".
 *   What we pay …      the operator's fee on this slot AS AGREED, then every
 *                      adjustment to it named the way the dashboard's own
 *                      Order Ledger names it, then the total.
 *
 * Neither is on the board: a list of a month of trips is not where the
 * office reads prices or partners' telephone numbers. The trip screen is
 * where one trip is dealt with, and the same person is already reading all
 * of it — prices, ledger, payments, partner — on the dashboard.
 *
 * What is still NOT here: no CUSTOMER price and no margin, because the
 * payload has neither; no acknowledge button, because acknowledging a job is
 * the operator's; no expense form, because a dispatcher files nothing; no
 * step buttons, because the steps are the driver's own taps.
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Link, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import useBackTo, { isPlainClick } from '../../../Hooks/useBackTo';
import { fetchDispatcherBoard, fetchDispatcherTrip } from '../../../Services/DispatcherPortalApi';
import { formatDate, formatTime } from '../../../Utils/timezone';
import {
  budapestDayWindow, hasProgramme, isNoShow, passengerSummary, progressIndex,
} from '../portalFormat';
import { startAssumingDriver } from '../../../Services/PortalApi';
import { fetchPassengerMessagePresets } from '../../../Services/PortalMessagePresetsApi';
import usePortalPrincipal from '../Operator/usePortalPrincipal';
import useAskSubject from './askSubject';
import PortalRouteMap from '../Components/PortalRouteMap';
import ProgressStrip from '../Components/ProgressStrip';
import OvertimeCard from '../Components/OvertimeCard';
import { ChevronLeftIcon } from '../Components/Icons';
import {
  CustomerCard, Facts, Manifest, Note, RouteOrProgramme,
} from '../Components/TripCards';
import {
  driverText,
  ENDPOINT_LABELS_EN,
  longDay,
  operatorLoadErrorMessage,
  OVERTIME_COPY_EN,
  PASSENGER_WORDS_EN,
  paxLabel,
  PROGRESS_STEPS_EN,
  roleLabel,
  ROUTE_MAP_COPY_EN,
  routeLabel,
  TRIP_CARD_COPY_EN,
  vehicleText,
} from '../Operator/operatorFormat';
import Pill from '../Operator/Pill';
import Toast from '../Components/Toast';
import AssignSheet from '../Operator/AssignSheet';
import { dispatcherAssignSource } from '../assignSources';
import CollapsibleSection from './CollapsibleSection';
import CrewSheet, { hasCrewActions } from './CrewSheet';
import NoShowEvidenceCard from './NoShowEvidenceCard';
import DriverContact from './DriverContact';
import PassengerMessage from './PassengerMessage';
import PassengerMoreOptions from './PassengerMoreOptions';
import OperatorFeeCard from './OperatorFeeCard';
import PartnerCard from './PartnerCard';
import {
  attentionOf,
  CLOSED_BY_OFFICE,
  currentStep,
  manifestCount,
  operatorName,
  overtimeCount,
  overtimeSummary,
  slotKey,
  stepText,
  stopsSummary,
} from './dispatcherFormat';

// A deep link has no row to go by, so the trip is looked for in the widest
// window the board takes (31 days): a fortnight either side of today.
const LOOKBACK_DAYS = 15;
const LOOKAHEAD_DAYS = 15;

const NOT_FOUND = 'This trip is not on the board. It may have been cancelled, '
  + 'or it may fall outside the last two weeks.';

const findIn = async (bookingId, range) => {
  const { trips } = await fetchDispatcherBoard(range);
  return trips.find((row) => row._id === bookingId) || null;
};

/**
 * The trip from one board read: its own Budapest day when the row is known,
 * else the window around today. Only a known row that has moved off its day
 * costs a second read, of the window.
 */
const findTrip = async (bookingId, known) => {
  const day = known?.date_datetime ? formatDate(known.date_datetime) : '';
  const found = day ? await findIn(bookingId, { from: day, to: day }) : null;
  return found || findIn(bookingId, budapestDayWindow(LOOKBACK_DAYS, LOOKAHEAD_DAYS));
};

/** Progress is the lead driver's: a guide's or a coordinator's slot has none. */
const showsProgress = (trip) => trip.status !== 'Cancelled' && !isNoShow(trip) && trip.lead;

/**
 * Who is on this trip, for which operator, in what — names, never ids.
 *
 * TWO TIERS OF ACTION, because they are not two of a kind. Reaching the
 * driver is the thing the office does from here most, and most urgently, so
 * Message and Call are ON the card and Message is the widest control on it.
 * CHANGING who is on the trip is occasional, and ACTING AS the driver is
 * rare, administrator-only and consequential; both are a tap further away,
 * behind More… (CrewSheet, which explains the split). What used to be five
 * chips of identical weight in one wrapped row is now one habit, one
 * exception and one door.
 *
 * The door says More… rather than naming one of the jobs behind it: it leads
 * to two or three unlike things, and the customer card beneath now has the
 * same door under the same word, so a second tier reads the same wherever
 * this screen offers one.
 */
function CrewCard({ trip, onAssign, onAdd }) {
  const navigate = useNavigate();
  const { principal } = usePortalPrincipal();
  const [managing, setManaging] = useState(false);

  const driverId = trip.driver?.id;
  // Administrators only: the API refuses everyone else, and this does not
  // offer what would be refused — nor on a trip with nobody to be.
  const mayAssume = Boolean(principal?.may_assume_driver && driverId);

  /**
   * Open this driver's own screen, as them — for the phone that is flat, the
   * driver who left it at home, the trip that has to be closed off now.
   *
   * Nothing here decides anything. The driver id goes to the API on every
   * subsequent request and the API resolves the whole principal from the
   * driver's own row, so the worst a wrong id can do is 404.
   */
  const viewAsDriver = () => {
    startAssumingDriver({ id: driverId, name: driverText(trip) });
    navigate('/portal', { replace: false });
  };

  return (
    <>
      <Facts
        title="Crew"
        rows={[
          // A booking nobody has taken on comes with no operator at all.
          { key: 'Operator', value: trip.operator ? operatorName(trip) : 'No operator assigned' },
          { key: 'Driver', value: driverText(trip) || 'No driver assigned' },
          { key: 'Vehicle', value: vehicleText(trip) },
          { key: 'Role', value: trip.role && trip.role !== 'driver' ? roleLabel(trip.role) : '' },
        ]}
      />
      {/* A named group: on a screen reader these are "crew actions", not
          three loose controls after a list of facts. */}
      <div className="pp-dispatch__crew-actions" role="group" aria-label="Crew actions">
        {/* The driver is named just above: this is where the office reaches
            them, and it is the biggest thing on the card. Nothing at all
            when there is no number on file. */}
        <DriverContact name={driverText(trip)} phone={trip.driver?.phone_number} lead />
        {/* ONE door to the occasional and the consequential. A closed trip
            that no administrator is looking at has nothing behind it, and
            says so where the control would have been — the board's own
            wording, from AssignButton. */}
        {hasCrewActions(trip, { mayAssume }) ? (
          <button
            type="button"
            className="pp-btn pp-btn--sm pp-btn--outline pp-btn--block"
            onClick={() => setManaging(true)}
            // The accessible name CONTAINS the visible label (WCAG 2.5.3) —
            // CrewSheet's own rule — so it names the crew for a screen
            // reader while the button itself stays one quiet word.
            aria-label={`More crew options on ${trip.booking_reference}`}
          >
            More…
          </button>
        ) : (
          <span className="pp-op-small pp-dispatch__closed">Closed</span>
        )}
      </div>

      {managing ? (
        <CrewSheet
          trip={trip}
          mayAssume={mayAssume}
          onClose={() => setManaging(false)}
          // Each hands the job back to the page and closes itself, so there
          // is never a sheet stacked on a sheet.
          onChange={() => { setManaging(false); onAssign(); }}
          onAdd={() => { setManaging(false); onAdd(); }}
          onViewAsDriver={() => { setManaging(false); viewAsDriver(); }}
        />
      ) : null}
    </>
  );
}

export default function DispatcherTripDetails() {
  const { bookingId } = useParams();
  const location = useLocation();
  const handed = location.state?.trip?._id === bookingId ? location.state.trip : null;
  /**
   * Back to the board AS IT WAS LEFT — the period that was chosen, the
   * search that was typed, the attention filter, and the row that was being
   * read. All of those live in the history entry behind this screen (its
   * query string, and its scroll in ScrollMemory), so this has to POP
   * rather than navigate to `/portal/dispatcher` afresh: a plain link
   * PUSHES a new entry — same path, no query string, scrolled to the top —
   * which looks like Back and undoes exactly what Back is for. It stays a
   * real link, so the destination is visible and cmd-click still opens the
   * board in a new tab.
   */
  const backToBoard = useBackTo('/portal/dispatcher');
  const onBack = useCallback((event) => {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    backToBoard();
  }, [backToBoard]);
  const [trip, setTrip] = useState(handed);
  /**
   * Tell the frame's assistant launcher which trip is open, BY ITS REFERENCE —
   * which is why this is here and not in the frame. `bookingId` from the URL
   * is a mongo ObjectId: nothing a dispatcher would say out loud and nothing
   * the agent could look up. The reference only exists once the trip has been
   * read, so until it has, the launcher stays its plain self.
   */
  useAskSubject('trip', trip?.booking_reference);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // '' | 'change' | 'add' — which sheet is open, and what it is for.
  const [assigning, setAssigning] = useState('');
  const [notice, setNotice] = useState('');
  // The partner and this slot's fee. Their own read, so the trip itself
  // still comes from the board — the one place that knows who is driving and
  // what step they are on.
  const [detail, setDetail] = useState(null);
  /**
   * The office's saved messages for this booking, variables already filled
   * in by the server. The composer's chips.
   *
   * Fetched HERE rather than inside the composer because the composer is a
   * node in the customer card's `primary` slot — a card shared with the
   * driver's and the operator's screens, which must not read these at all
   * (the endpoint 403s both). Keeping the fetch on the dispatcher's screen
   * keeps that true by construction rather than by a prop nobody passes.
   *
   * Empty until it lands, and empty for good if it fails: snippets are a
   * convenience, never a precondition for typing a message.
   */
  const [presets, setPresets] = useState([]);

  // The row as handed over, for the read below — a ref, not a dependency:
  // the read happens once per trip, not again whenever state changes.
  const known = useRef(handed);
  known.current = handed;

  useEffect(() => {
    let cancelled = false;
    setTrip(known.current);
    setLoading(true);
    setError('');
    findTrip(bookingId, known.current)
      .then((found) => {
        if (cancelled) return;
        setTrip(found);
        if (!found) setError(NOT_FOUND);
      })
      .catch((failure) => {
        // Keep a handed-over row on screen; say it could not be refreshed.
        if (!cancelled) setError(operatorLoadErrorMessage(failure));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [bookingId]);

  /**
   * The partner and this slot's fee, for the trip once it is known.
   *
   * Keyed on the SLOT, not the booking: a trip with a driver and a guide is
   * two slots with two fees, and moving the trip to another operator gives
   * it a third. A failure is quiet — the trip is still worth reading with no
   * Sold-by card on it, and the error strip above is for the trip itself.
   */
  const slot = trip ? slotKey(trip) : '';

  useEffect(() => {
    if (!trip) return undefined;
    let cancelled = false;
    setDetail(null);
    fetchDispatcherTrip(trip)
      .then((found) => { if (!cancelled) setDetail(found); })
      .catch(() => { if (!cancelled) setDetail(null); });
    return () => { cancelled = true; };
    // `trip` itself is a new object on every board read; the SLOT is what the
    // answer is about, so a re-read that brings back the same slot asks
    // nothing. (The effect above does the same with `bookingId`.)
  }, [slot]);

  /**
   * The saved messages, once the booking is known.
   *
   * Keyed on the BOOKING and not the slot: the library is the same whichever
   * slot of a trip is open, and the variables resolve off the booking. So
   * changing operator does not re-read it.
   *
   * A FAILURE IS SILENT AND LEAVES THE LIST EMPTY. The composer renders no
   * chip row at all with an empty list, and everything else in it still
   * works — somebody who came to type one line must not be shown an error
   * about a list they were not going to use, and must certainly not be
   * blocked from sending. The error strip above is for the trip itself.
   */
  useEffect(() => {
    if (!bookingId) return undefined;
    let cancelled = false;
    setPresets([]);

    fetchPassengerMessagePresets(bookingId)
      .then((payload) => { if (!cancelled) setPresets(payload.presets); })
      .catch(() => { if (!cancelled) setPresets([]); });

    return () => { cancelled = true; };
  }, [bookingId]);

  if (!trip) {
    return (
      <div className="pp-op-page pp-op-trip">
        <Link className="pp-op-back" to="/portal/dispatcher" onClick={onBack}>
          <ChevronLeftIcon size={18} width={2.5} />
          Dispatch board
        </Link>
        {loading ? <div className="pp-state" role="status">Loading the trip…</div> : null}
        {!loading ? <div className="pp-op-error" role="alert">{error || NOT_FOUND}</div> : null}
      </div>
    );
  }

  const step = currentStep(trip);
  const attention = attentionOf(trip);
  const when = `${longDay(trip.date_datetime)} · ${formatTime(trip.date_datetime)}`;

  return (
    <div className="pp-op-page pp-op-trip pp-dispatch__trip-page">
      <Link className="pp-op-back" to="/portal/dispatcher" onClick={onBack}>
        <ChevronLeftIcon size={18} width={2.5} />
        Dispatch board
      </Link>

      <div className="pp-op-page__head">
        <div>
          <h1 className="pp-op-title">{trip.booking_reference}</h1>
          <div className="pp-op-subtitle">{`${when} · ${routeLabel(trip)}`}</div>
        </div>
        <div className="pp-op-page__tools">
          <Pill tone={step.tone}>{stepText(step)}</Pill>
          {step.closedByOffice ? <Pill tone="muted">{CLOSED_BY_OFFICE}</Pill> : null}
          {attention ? <Pill tone={attention.tone}>{attention.label}</Pill> : null}
        </div>
      </div>

      {error ? <div className="pp-op-error" role="alert">{error}</div> : null}

      {/* Above everything, because it is why this trip is being looked at. */}
      <NoShowEvidenceCard trip={trip} />

      {showsProgress(trip) ? (
        <div className="pp-card pp-op-trip__progress">
          <ProgressStrip
            index={progressIndex(trip)}
            steps={PROGRESS_STEPS_EN}
            label="Trip progress"
          />
        </div>
      ) : null}

      <div className="pp-op-trip__grid">
        <div className="pp-stack pp-op-trip__main">
          {/* The heavy ones fold. Each says what it holds while closed —
              a name and a count where there is one — and each remembers the
              person's own answer, so a dispatcher who always wants the map
              open gets it open next time. */}
          <CollapsibleSection remember="dispatch.map" title="Route map" defaultOpen>
            <PortalRouteMap
              trip={trip}
              target={{ point: trip.from, label: ENDPOINT_LABELS_EN.from }}
              copy={ROUTE_MAP_COPY_EN}
            />
          </CollapsibleSection>

          <CollapsibleSection
            remember="dispatch.route"
            title={hasProgramme(trip) ? 'Programme' : 'Route'}
            summary={stopsSummary(trip)}
            defaultOpen
          >
            <RouteOrProgramme trip={trip} copy={TRIP_CARD_COPY_EN} />
          </CollapsibleSection>

          <Facts
            rows={[
              {
                key: 'Passengers',
                value: passengerSummary(trip, PASSENGER_WORDS_EN) || paxLabel(trip.passengers),
              },
              { key: 'Category', value: trip.category },
              { key: 'Flight', value: trip.flight_number },
              { key: 'Event', value: trip.event_name },
            ]}
          />

          {manifestCount(trip) ? (
            <CollapsibleSection
              remember="dispatch.manifest"
              title="Passenger manifest"
              summary={String(manifestCount(trip))}
            >
              <Manifest trip={trip} copy={TRIP_CARD_COPY_EN} />
            </CollapsibleSection>
          ) : null}

          {overtimeCount(trip) ? (
            <CollapsibleSection
              remember="dispatch.overtime"
              title="Paid waiting"
              summary={overtimeSummary(trip)}
            >
              <OvertimeCard trip={trip} copy={OVERTIME_COPY_EN} />
            </CollapsibleSection>
          ) : null}
        </div>

        <div className="pp-stack pp-op-trip__side">
          <CrewCard
            trip={trip}
            onAssign={() => setAssigning('change')}
            onAdd={() => setAssigning('add')}
          />

          {/* What we pay the operator on THIS slot, adjustments named. */}
          <OperatorFeeCard fee={detail?.fee} operator={trip.operator ? operatorName(trip) : ''} />

          {/* THE DISPATCHER'S OWN PASSENGER CARD, and the only one of the
              three screens that renders this card which fills these slots.
              Writing AS THE COMPANY leads (PassengerMessage: the company
              number, the shared inbox), and the `wa.me` deep link — the
              sender's own chat, replies to their own phone — is a tap away
              behind More options (PassengerMoreOptions). Call is untouched,
              in the middle of the card where it has always been. */}
          <CustomerCard
            trip={trip}
            copy={TRIP_CARD_COPY_EN}
            primary={<PassengerMessage trip={trip} presets={presets} />}
            more={<PassengerMoreOptions trip={trip} />}
          />

          {/* Who sold it, and how to reach them. */}
          <PartnerCard partner={detail?.partner} />

          <Note label="Office note" body={trip.operator_comments} />
        </div>
      </div>

      {assigning ? (
        <AssignSheet
          trip={trip}
          source={dispatcherAssignSource}
          add={assigning === 'add'}
          onClose={() => setAssigning('')}
          onAssigned={(updated) => {
            setTrip(updated);
            setAssigning('');
            setNotice(`${updated?.booking_reference}: saved, the customer has been notified`);
          }}
        />
      ) : null}

      <Toast message={notice} onDone={() => setNotice('')} />
    </div>
  );
}
